/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, September 26th 2021, 6:51:16 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { WritableModel } from "lib/schemas/abstract";
import {
  schemaBuilder,
  fileBaseSchema,
  nestedOne2ManyBaseSchema,
  Many2ManyIntPKBaseSchema,
  Many2ManyUUIDPKBaseSchema,
  validateManyAtLeastOne,
} from "lib/validation";
import { Department, DepartmentValidationSchema } from "lib/schemas/organizations/Department";
import { User, Portal } from "lib/schemas";
import { PrimaryKeyValue } from "lib/types";
import { isPhoneNumber } from "lib/validation";
import { PaymentMethod } from "lib/schemas";

export enum ORGANIZATION_STATUS {
  active = "Active",
  pending = "Pending",
  deactivated = "Deactivated",
  suspended = "Suspended",
  blacklisted = "Blacklisted",
}

interface OrganizationColorScheme {
  primary: string;
  secondary: string;
}

const OrganizationColorSchemeValidationSchema: schemaBuilder.SchemaOf<OrganizationColorScheme> = schemaBuilder
  .object()
  .shape({
    primary: schemaBuilder.string().required("Primary color is required"),
    secondary: schemaBuilder.string().required("Secondary color is required"),
  });

export interface Organization extends WritableModel<true> {
  name: string;
  slogan?: string;
  sector?: string;
  logo?: string;
  color_scheme?: OrganizationColorScheme;
  street?: string;
  office?: string;
  postal_address?: string;
  city?: string;
  country?: number;
  readonly country_iso_alpha2_code?: string;
  phone?: string;
  email?: string;
  default_currency: string;
  department_set: Department[];
  readonly portals?: Portal["id"][];
  workers: PrimaryKeyValue<User>[];
  organization_admins: PrimaryKeyValue<User>[];
  readonly organization_status?: keyof typeof ORGANIZATION_STATUS;
  readonly creation_date?: Date;
  readonly activation_date?: Date;
  readonly is_main_organization?: boolean;
  payment_methods: PaymentMethod["id"][];
  website?: string;
}

export type OrganizationListItem = Pick<
  Organization,
  "id" | "name" | "email" | "phone" | "default_currency" | "organization_status" | "logo"
>;

export const newOrganizationDefault: Organization = {
  name: "",
  department_set: [],
  workers: [],
  organization_admins: [],
  default_currency: null,
  payment_methods: [],
};

type WriteableOrganization = Omit<
  Organization,
  | "organization_status"
  | "country_iso_alpha2_code"
  | "creation_date"
  | "activation_date"
  | "portals"
  | "is_main_organization"
>;

export const OrganizationValidationSchema: schemaBuilder.SchemaOf<WriteableOrganization> = schemaBuilder
  .object()
  .shape({
    id: schemaBuilder.string().uuid().nullable(),
    name: schemaBuilder.string().required("Organization Name is required."),
    sector: schemaBuilder.string().uuid().nullable().required("Industry is required"),
    slogan: schemaBuilder.string().optional(),
    logo: fileBaseSchema({ sizeLimit: 2097152 }).nullable(),
    color_scheme: OrganizationColorSchemeValidationSchema.optional().nullable().default(null),
    street: schemaBuilder.string().nullable(),
    office: schemaBuilder.string().nullable(),
    postal_address: schemaBuilder.string().nullable(),
    city: schemaBuilder.string().nullable(),
    country: schemaBuilder.number().nullable().required("Country is required"),
    phone: schemaBuilder
      .string()
      .nullable()
      .test("valid-phone-number", "Please provide a valid phone number", (v) => isPhoneNumber(v)),
    email: schemaBuilder.string().email().nullable().required("Email is required"),
    website: schemaBuilder.string().nullable(),
    default_currency: schemaBuilder.string().required("Please set your default currency"),
    workers: Many2ManyIntPKBaseSchema().test(
      "at-least-one-worker",
      "You must have at least one worker for your organization.",
      validateManyAtLeastOne
    ),
    organization_admins: Many2ManyIntPKBaseSchema().test(
      "at-least-one-admin",
      "You must appoint at least one admnistrator for your organization.",
      validateManyAtLeastOne
    ),
    department_set: nestedOne2ManyBaseSchema({ relatedSchema: DepartmentValidationSchema }).test(
      "at-least-one-department",
      "You must provide at least one department for your organization",
      validateManyAtLeastOne
    ),
    payment_methods: Many2ManyUUIDPKBaseSchema().optional().nullable(),
    tags: Many2ManyUUIDPKBaseSchema().optional().nullable(),
  });
